import * as React from 'react'

import { useQuery } from '@apollo/client/react'
import type { ErrorLike } from '@apollo/client'

import { graphql } from '~publish/graphql'
import type { VariablesOf } from '~publish/graphql'

import { ThreadMessage_ConversationMessage } from '../fragments/threadMessage'
import type { ThreadMessageData } from '../fragments/threadMessage'

export const MESSAGES_PER_PAGE = 20

export const ConversationThreadQueryGQL = graphql(
  /* GraphQL */ `
    query ConversationThread(
      $input: ConversationMessagesInput!
      $first: Int!
      $after: String
    ) {
      conversationMessages(input: $input, first: $first, after: $after) {
        edges {
          cursor
          node {
            ...ThreadMessage_ConversationMessage
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `,
  [ThreadMessage_ConversationMessage],
)

type ConversationThreadQueryVariables = VariablesOf<
  typeof ConversationThreadQueryGQL
>

type UseConversationMessagesQueryProps = {
  /** The open conversation. `undefined` skips the query. */
  conversationId: string | undefined
}

export type UseConversationMessagesQueryReturn = {
  /** Oldest first, so the thread reads top to bottom and ends at the latest. */
  messages: ThreadMessageData[]
  loading: boolean
  error: ErrorLike | undefined
  /** Reloads the newest page, for the error state's retry. */
  refetch: () => Promise<unknown>
}

/**
 * The newest page of a conversation's messages.
 *
 * The API returns messages newest first and pages back in time with `after`.
 * The thread shows them the other way round, so the edges are reversed here
 * and nowhere else. Only the first page is read: loading older pages is a
 * follow-up, and the Apollo type policy for `conversationMessages` already
 * appends them when it lands.
 *
 * Keyed on the conversation id, so selecting another conversation runs a new
 * query rather than reshaping this one; `loading` is true until its first page
 * arrives, which is what lets the thread pane swap without showing a stale
 * thread under a new header.
 */
export function useConversationMessagesQuery({
  conversationId,
}: UseConversationMessagesQueryProps): UseConversationMessagesQueryReturn {
  const variables = React.useMemo<ConversationThreadQueryVariables>(
    () => ({
      input: { conversationId: conversationId ?? '' },
      first: MESSAGES_PER_PAGE,
      after: null,
    }),
    [conversationId],
  )

  const { data, loading, error, refetch } = useQuery(
    ConversationThreadQueryGQL,
    {
      variables,
      fetchPolicy: 'cache-and-network',
      skip: !conversationId,
    },
  )

  const messages = React.useMemo<ThreadMessageData[]>(
    () =>
      data?.conversationMessages.edges.map((edge) => edge.node).toReversed() ??
      [],
    [data],
  )

  // A failed reload already surfaces through `error`; swallowing the
  // rejection keeps a second failure from also logging as unhandled.
  const refetchNewest = React.useCallback(
    (): Promise<unknown> => refetch(variables).catch(() => undefined),
    [refetch, variables],
  )

  return React.useMemo(
    () => ({
      messages,
      loading,
      error,
      refetch: refetchNewest,
    }),
    [messages, loading, error, refetchNewest],
  )
}
